import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getToken, logout } from '@/services/auth.js';
import { getMatchedPhotos } from '@/services/api.js';
import ImageGrid from './ImageGrid';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";

const GalleryView = ({ uuid, eventDetails, lazyLoad = true }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    // Lightbox states
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);

    const observer = useRef();
    const loaderRef = useRef();
    const token = getToken();
    const LIMIT = 2;

    const loadImages = async (currentOffset = 0) => {
        if (!token || loading || (!hasMore && currentOffset > 0)) return;

        setLoading(true);
        try {
            const response = await getMatchedPhotos(token, uuid, currentOffset);

            if (response.result) {
                setImages(prev => currentOffset === 0 ? response.data.results : [...prev, ...response.data.results]);
                setHasMore(response.data.results.length === LIMIT);
            } else if (response.error_code === "AUTH_INVALID_CREDENTIALS" || response.status === 401) {
                logout();
                window.location.reload();
            } else {
                setError(response.message || 'Failed to load images');
            }
        } catch (err) {
            console.log(err)
            setError('Failed to load images');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    const loadMore = () => {
        if (hasMore && !loading) {
            setOffset(prevOffset => prevOffset + LIMIT);
        }
    };

    const handleObserver = useCallback(entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
            loadMore();
        }
    }, [hasMore, loading]);

    useEffect(() => { loadImages(offset); }, [offset]);

    useEffect(() => {
        if (!lazyLoad || !loaderRef.current) return;
        observer.current = new IntersectionObserver(handleObserver, { rootMargin: '100px' });
        observer.current.observe(loaderRef.current);
        return () => observer.current?.disconnect();
    }, [handleObserver, lazyLoad]);

    const handleImageClick = (imageIndex) => {
        setIndex(imageIndex);
        setOpen(true);
    };

    const slides = images.map(img => ({
        src: img.image_url,
        alt: "",
        downloadUrl: `https://app.snoxpro.com/proxy/download/?url=${img.image_url}`,
        downloadFilename: `snox-photo-${img.id}.jpg`
    }));

    if (!token) return (
        <div className="auth-required text-center mt-4">
            <i className="fa fa-lock fa-3x"></i>
            <h3 className="mt-3">Authentication Required</h3>
        </div>
    );

    if (error) return (
        <div className="error-container text-center">
            <i className="fa fa-exclamation-triangle fa-3x"></i>
            <h3 className="mt-3">{error}</h3>
            <button className="button mt-3" onClick={() => loadImages(0)}>
                <i className="fa fa-refresh"></i> Try Again
            </button>
        </div>
    );

    return (
        <div className="gallery-container">
            <div className="gallery-header">
                <h2 className="text-center">{eventDetails?.title || 'Your Photos'}</h2>
                <p className="text-center mb-4">AI-matched photos</p>
                {images.length > 0 && (
                    <div className="photo-count text-center mb-2">
                        <span>{images.length} photos found</span>
                    </div>
                )}
            </div>

            {initialLoading ? (
                <div className="gallery-loading">
                    <div className="spinner"></div>
                    <p>Loading your photos...</p>
                </div>
            ) : (
                <>
                    <ImageGrid
                        images={images}
                        onImageClick={handleImageClick}
                    />

                    {loading && images.length > 0 && (
                        <div className="text-center mt-4"><div className="spinner"></div></div>
                    )}

                    {!lazyLoad && !loading && hasMore && images.length > 0 && (
                        <div className="text-center mt-4">
                            <button className="button" onClick={loadMore}>Load More</button>
                        </div>
                    )}

                    {!loading && images.length === 0 && (
                        <div className="empty-gallery text-center my-5 py-4">
                            <i className="fa fa-image fa-3x mb-3"></i>
                            <h3>No Photos Found</h3>
                            <button className="button mt-3" onClick={() => loadImages(0)}>
                                <i className="fa fa-refresh"></i> Refresh
                            </button>
                        </div>
                    )}

                    {lazyLoad && <div ref={loaderRef} style={{ height: '20px' }} />}

                    {/* Lightbox Component */}
                    <Lightbox
                        open={open}
                        close={() => setOpen(false)}
                        index={index}
                        slides={slides}
                        plugins={[Thumbnails, Zoom, Download, Counter]}
                        counter={{ container: { style: { top: '30px' } } }}
                        thumbnails={{ position: "bottom" }}
                        zoom={{ maxZoomPixelRatio: 5 }}
                        on={{
                            view: ({ index }) => setIndex(index)
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default GalleryView;